import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { Mail, KeyRound, Lock, Eye, EyeOff, ArrowLeft } from 'lucide-react';
import toast from 'react-hot-toast';
import { authService } from '../services/auth';
import { ButtonLoader } from '../components/LoadingStates';
import EngiSpiderIcon from '../assets/EngiSpider_Icon.png';


export const ForgotPassword: React.FC = () => {
  const { currentTheme } = useTheme();
  const navigate = useNavigate();

  const [step, setStep] = useState<1 | 2>(1);
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSendOTP = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) { toast.error('Please enter your email address'); return; }
    try {
      setLoading(true);
      await authService.sendForgotPasswordOTP(email.trim());
      toast.success('OTP sent to your email');
      setStep(2);
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Failed to send OTP. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!otp.trim()) { toast.error('Please enter the OTP'); return; }
    if (otp.length !== 6) { toast.error('OTP must be 6 digits'); return; }
    if (!newPassword) { toast.error('Please enter a new password'); return; }
    if (newPassword.length < 6) { toast.error('Password must be at least 6 characters'); return; }
    if (newPassword !== confirmPassword) { toast.error('Passwords do not match'); return; }
    try {
      setLoading(true);
      await authService.resetPasswordWithOTP(email.trim(), otp.trim(), newPassword);
      toast.success('Password reset successfully! Please sign in.');
      navigate('/login');
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Invalid OTP or reset failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResendOTP = () => {
    setLoading(true);
    authService.sendForgotPasswordOTP(email)
      .then(() => toast.success('OTP resent!'))
      .catch(() => toast.error('Failed to resend OTP'))
      .finally(() => setLoading(false));
  };

  return (
    <div className="min-h-screen flex">
      {/* Left side - Form */}
      <div className="flex-1 flex items-center justify-center px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-md w-full space-y-8">
          <div>
            <div className="flex items-center justify-between mb-2">
              <button
                type="button"
                onClick={() => step === 2 ? setStep(1) : navigate('/login')}
                className="flex items-center gap-1.5 text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors"
              >
                <ArrowLeft className="h-4 w-4" />
                {step === 2 ? 'Change email' : 'Back to login'}
              </button>
              <img src={EngiSpiderIcon} alt="EngiSpider" className="h-10 w-auto" />
            </div>
            <h2 className="mt-6 text-3xl font-bold text-gray-900">
              {step === 1 ? 'Forgot Password?' : 'Reset Password'}
            </h2>
            <p className="mt-2 text-sm text-gray-600">
              {step === 1
                ? "Enter your email address and we'll send you a 6-digit OTP."
                : `Enter the OTP sent to ${email} and choose a new password.`}
            </p>
          </div>

          {step === 1 ? (
            <form className="mt-8 space-y-6" onSubmit={handleSendOTP}>
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                  Email Address
                </label>
                <div className="mt-1 relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Mail className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    id="email"
                    type="email"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    placeholder="Enter your email"
                    className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>
              </div>

              <div>
                <button
                  type="submit"
                  disabled={loading}
                  className="group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-md text-white focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  style={{ backgroundColor: currentTheme.colors.primary[600] }}
                  onMouseEnter={e => !loading && (e.currentTarget.style.backgroundColor = currentTheme.colors.primary[700])}
                  onMouseLeave={e => !loading && (e.currentTarget.style.backgroundColor = currentTheme.colors.primary[600])}
                >
                  {loading ? <ButtonLoader text="Sending OTP..." /> : 'Send OTP'}
                </button>
              </div>
            </form>
          ) : (
            <form className="mt-8 space-y-6" onSubmit={handleResetPassword}>
              <div className="space-y-4">
                {/* OTP */}
                <div>
                  <label htmlFor="otp" className="block text-sm font-medium text-gray-700">
                    6-Digit OTP
                  </label>
                  <div className="mt-1 relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <KeyRound className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      id="otp"
                      type="text"
                      inputMode="numeric"
                      maxLength={6}
                      value={otp}
                      onChange={e => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      placeholder="Enter OTP"
                      className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary-500 focus:border-primary-500 tracking-widest text-center font-bold"
                    />
                  </div>
                </div>

                {/* New Password */}
                <div>
                  <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700">
                    New Password
                  </label>
                  <div className="mt-1 relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Lock className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      id="newPassword"
                      type={showPassword ? 'text' : 'password'}
                      value={newPassword}
                      onChange={e => setNewPassword(e.target.value)}
                      placeholder="Min 6 characters"
                      className="block w-full pl-10 pr-10 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(p => !p)}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    >
                      {showPassword
                        ? <EyeOff className="h-5 w-5 text-gray-400" />
                        : <Eye className="h-5 w-5 text-gray-400" />}
                    </button>
                  </div>
                </div>

                {/* Confirm Password */}
                <div>
                  <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700">
                    Confirm Password
                  </label>
                  <div className="mt-1 relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Lock className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      id="confirmPassword"
                      type={showPassword ? 'text' : 'password'}
                      value={confirmPassword}
                      onChange={e => setConfirmPassword(e.target.value)}
                      placeholder="Repeat new password"
                      className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>
                </div>
              </div>

              <div className="space-y-3">
                <button
                  type="submit"
                  disabled={loading}
                  className="group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-md text-white focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  style={{ backgroundColor: currentTheme.colors.primary[600] }}
                  onMouseEnter={e => !loading && (e.currentTarget.style.backgroundColor = currentTheme.colors.primary[700])}
                  onMouseLeave={e => !loading && (e.currentTarget.style.backgroundColor = currentTheme.colors.primary[600])}
                >
                  {loading ? <ButtonLoader text="Resetting..." /> : 'Reset Password'}
                </button>

                <button
                  type="button"
                  onClick={handleResendOTP}
                  disabled={loading}
                  className="w-full text-sm font-medium text-center transition-colors disabled:opacity-50"
                  style={{ color: currentTheme.colors.primary[600] }}
                >
                  Resend OTP
                </button>
              </div>
            </form>
          )}
        </div>
      </div>

      {/* Right side */}
      <div className="hidden lg:flex lg:w-2/5 items-center justify-center bg-gradient-to-br from-primary-600 to-primary-800">
        <div className="text-center text-white px-12">
          <KeyRound className="h-20 w-20 mx-auto mb-6 opacity-80" />
          <h3 className="text-2xl font-bold mb-3">Secure Password Reset</h3>
          <p className="text-sm opacity-80 leading-relaxed">
            We'll send a one-time password to your registered email address. Use it to set a new password for your account.
          </p>
        </div>
      </div>
    </div>
  );
};
